// Translation strings for English (en).
//
// Split out of the former single translations.js so the three languages can be
// edited without colliding. t() has NO fallback - a key missing here renders as
// the raw key name, so every language must define every key.
// `npm run check:translations` asserts that.

const en = {
  // Common
  cancel: 'Cancel',
  save: 'Save',
  delete: 'Delete',
  edit: 'Edit',
  add: 'Add',
  close: 'Close',
  search: 'Search',
  loading: 'Loading...',
  error: 'Error',
  success: 'Success',

  // Navigation
  login: 'Login',
  logout: 'Logout',
  adminPanel: 'Admin Panel',
  publicView: 'Public View',
  settings: 'Settings',
  account: 'Account',
  menu: 'Menu',

  // User Management
  username: 'Username',
  password: 'Password',
  email: 'Email',
  role: 'Role',
  superAdmin: 'Super admin',
  countryAdmin: 'Country admin',
  editor: 'Editor',
  manageUsers: 'Manage Users',
  changePassword: 'Change Password',
  newPassword: 'New Password',
  confirmPassword: 'Confirm Password',
  currentPassword: 'Current Password',
  allFieldsRequired: 'All fields are required',
  passwordsDoNotMatch: 'New passwords do not match',
  passwordTooShort: 'New password must be at least 6 characters',
  passwordChangedSuccessfully: 'Password changed successfully!',
  failedToChangePassword: 'Failed to change password',
  connectionError: 'Connection error',
  saveFailed: 'Save failed',
  userManagement: 'User Management',
  backToAdmin: 'Back to Admin',
  addNewUser: 'Add New User',
  emailOptional: 'Email (optional)',
  rolePermissions: 'Role Permissions:',
  editorRole: 'Editor:',
  editorDescription: 'Series, races, drivers and results in their own country (no access to users)',
  superAdminRole: 'Super admin:',
  superAdminDescription: 'Every country, and the only role that can appoint another super admin',
  countryAdminRole: 'Country admin:',
  countryAdminDescription: 'Everything a super admin can do, in their own country only',
  countryAdminScopeHint: 'You administer one country. Series, events and accounts elsewhere belong to their own administrators.',
  managedBySuperAdmin: 'Managed by a super admin',
  addUser: 'Add User',
  existingUsers: 'Existing Users',
  created: 'Created:',
  lastLogin: 'Last login:',
  usernameAndPasswordRequired: 'Username and password are required',
  userCreatedSuccessfully: 'User created successfully',
  failedToCreateUser: 'Failed to create user',
  deleteThisUser: 'Delete this user?',
  failedToDeleteUser: 'Failed to delete user',
  editUser: 'Edit User',
  usernameRequired: 'Username is required',
  userUpdatedSuccessfully: 'User updated successfully',
  failedToUpdateUser: 'Failed to update user',
  resetPassword: 'Reset Password',
  resetPasswordFor: 'Reset password for',
  minimumSixCharacters: 'Minimum 6 characters',
  passwordResetSuccessfully: 'Password reset successfully',
  failedToResetPassword: 'Failed to reset password',

  // Series Management
  series: 'Series',
  seriesSettings: 'Series Settings',
  selectSeries: 'Select Series',
  selectYear: 'Select Year',
  noSeriesForYear: 'No series for year',
  createSeries: 'Create New Series',
  seriesName: 'Series Name',
  startYear: 'Start Year',
  endYear: 'End Year',
  yearHint: 'End year is optional. Use it for winter series spanning two years (e.g., 2024-2025)',
  racingSeries: 'Racing Series',
  newSeries: 'New Series',
  noSeriesYet: 'No series yet. Create your first one!',
  deleteSelectedSeries: 'Delete Selected Series',
  deleteThisSeries: 'Delete this series and all its data?',
  editSeries: 'Edit Series',
  seriesNameRequired: 'Series name is required',
  seriesClass: 'Class',
  seriesClasses: 'Classes',
  seriesClassHelp: 'A series can run several classes. Each keeps its own drivers, races and standings.',
  addSeriesClass: 'Add class',
  seriesClassName: 'Class name',
  seriesClassNameRequired: 'Class name is required',
  deleteSeriesClassConfirm: 'Delete this class, along with its races and results?',
  seriesClassLastCannotDelete: 'A series must keep at least one class.',
  seriesClassHasResults: 'This class has recorded results. Remove them first.',
  seriesClassNameTaken: 'A class with that name already exists in this series',
  classSettings: 'Class settings',
  inheritFromSeries: 'Follow the series',
  inheritedValue: 'Series setting',
  addExistingDriver: 'Add a driver from this series',
  removeFromClass: 'Remove from this class',
  removeFromClassConfirm: 'Remove this driver from this class? Their results elsewhere are untouched.',
  deleteDriverEverywhere: 'Delete from the whole series',
  deleteDriverEverywhereConfirm: 'Delete this driver from every class in the series, along with all their results?',
  carNumberTakenInClass: 'That car number already belongs to someone else in this class',
  driverNotInClass: 'That driver does not race in this series',
  noDriversInThisClass: 'No drivers in this class',
  country: 'Country',
  // Groups the country pickers are divided into. Looked up dynamically as
  // continent + the group name, so check-translations.js cannot see these -
  // countryGroups.test.js asserts all eight exist in all three languages.
  continentNordic: 'Nordics & Baltics',
  continentEurope: 'Europe',
  continentAfrica: 'Africa',
  continentAsia: 'Asia',
  continentNorthAmerica: 'North America',
  continentSouthAmerica: 'South America',
  continentOceania: 'Oceania',
  continentAntarctica: 'Antarctica',
  countryRequired: 'Country is required',
  create: 'Create',
  update: 'Update',
  totalRaces: 'Total Races',
  racesCounted: 'Races Counted',
  qualificationMaster: 'TQ Master',
  calculateQualificationPoints: 'Calculate Qualification Points',
  juniorChampionship: 'Junior Championship',
  seniorChampionship: 'Senior Championship',
  ageLimit: 'Age Limit',
  totalRacesDescription: 'How many races the series will have in total',
  racesCountedDescription: 'How many best results count towards the championship. 0 = count all races.',
  racesInSeries: 'races in series',
  best: 'best',
  countedLabel: 'counted',
  qualificationPointsDescription: 'Top 5 qualifiers get 5, 4, 3, 2, 1 bonus points',
  qualificationMasterDescription: '🏆 Separate championship for qualification-only points',
  enable: 'Enable',
  yearsAndUnder: 'years and under',
  yearsOld: 'years old',
  plusYearsOld: '+ years old',
  driversAgeAndUnder: 'Drivers {age} years and under',
  driversPlusYearsOld: 'Drivers {age}+ years old',
  tip: 'Tip:',
  changesAutoSaved: 'Changes are saved automatically. The standings will update immediately to reflect your settings.',
  exportChampionshipDataToExcel: 'Export championship data to Excel',
  pleaseSelectAtLeastOneSeries: 'Please select at least one series to export',
  exportFailed: 'Export failed',
  failedToExportChampionship: 'Failed to export championship data. Please try again.',
  sessionExpired: 'Session expired. Please log in again.',
  noSeriesAvailable: 'No series available',

  // Driver Management
  drivers: 'Drivers',
  driverName: 'Driver Name',
  birthYear: 'Birth Year',
  club: 'Club',
  addDriver: 'Add Driver',
  editDriver: 'Edit Driver',
  newDriver: 'New Driver',
  carNumberOptional: 'Car Number (optional)',
  clubOptional: 'Club (optional)',
  birthYearOptional: 'Birth Year (optional)',
  noDriversYet: 'No drivers yet.',
  deleteThisDriver: 'Delete this driver?',
  failedToRemoveFromClass: 'Failed to remove the driver from this class',
  selectAll: 'Select All',
  deselectAll: 'Deselect All',
  deleteSelected: 'Delete Selected',
  deleteSelectedDrivers: 'Delete {count} selected driver(s)?',
  copyDrivers: 'Copy Drivers',
  copyFromOtherSeries: 'Copy from other series',
  copyDriversFromOtherSeries: 'Copy Drivers from Other Series',
  selectSeriesToCopyFrom: 'Select series to copy drivers from',
  noOtherSeriesAvailable: 'No other series available',
  copyingFrom: 'Copying from',
  selectDriversToCopy: 'Select drivers to copy',
  noDriversInThisSeries: 'No drivers in this series',
  copySelected: 'Copy Selected',
  back: 'Back',
  driversCopiedSuccessfully: '{count} driver(s) copied successfully',
  failedToCopyDrivers: 'Failed to copy drivers',

  // Race Management
  races: 'Races',
  raceName: 'Race Name',
  raceDate: 'Date',
  raceSequenceNumber: 'Race label (e.g. SM2, Round 1)',
  addRace: 'Add Race',
  editRace: 'Edit Race',
  raceResults: 'Race Results',
  addResults: 'Add Results',
  newRace: 'New Race',
  deleteThisRace: 'Delete this race?',
  pleaseSelectSeriesFirst: 'Please select a series first',
  errorCreatingRace: 'Error creating race: ',
  editResults: 'Edit Results',
  importFromExcel: 'Import from Excel',
  importFromMyRCM: 'Import from MyRCM',
  // LiveRC import. The liverc* error keys are handed to t() by name from
  // parseLiveRCUrl in utils/liverc.js, so check-translations.js cannot see them
  // being used - liverc.test.js asserts they all resolve instead.
  importFromLiveRC: 'Import from LiveRC',
  howToImportFromLiveRC: 'How to import from LiveRC:',
  livercStep1: "Open the event on the track's own LiveRC site, for example gandsraceway.liverc.com",
  livercStep2: 'Copy the address of the event page and paste it below',
  livercStep3: 'Pick the class this race is, check the driver matches, then import',
  livercUrlLabel: 'LiveRC event address',
  livercUrlHint: 'The event page, or its Final Results page. Every class in the event is read from it.',
  livercFetching: 'Reading LiveRC...',
  livercPickClass: 'Class',
  livercPickClassPlaceholder: 'Choose the class for this race',
  livercNoQualifying: 'This event has no qualifying rankings, so qualification points are left empty.',
  livercCreateNewDriver: 'Create a new driver',
  livercLabel: 'LiveRC:',
  livercEventLoaded: 'Found {count} class(es) in this event',
  livercImported: 'Imported {count} results for {class}',
  livercFetchFailed: 'Could not reach LiveRC',
  livercEventNotFound: 'That event has no published results',
  livercNoResultsTable: 'That page has no results table',
  livercUrlRequired: 'Paste a LiveRC event address first',
  livercNotALiveRCUrl: 'That is not a LiveRC address',
  livercNotAnEventUrl: 'That is a LiveRC page, but not an event page. Use the event or Final Results address.',
  livercNoEventId: 'That address has no event id in it',
  failedToCreateDriver: 'Failed to create driver',
  resultsAdminOnly: 'Results (Admin Only)',
  deleteRace: 'Delete Race',
  resetRaceResults: 'Reset Race Results',
  resetRaceResultsConfirm: 'Are you sure you want to reset all results for this race?',
  raceResultsReset: 'Race results reset successfully',
  failedToResetResults: 'Failed to reset results',
  editResultsFor: 'Edit Results:',
  driver: 'Driver',
  qualPos: 'Qual Pos',
  qualPts: 'Qual Pts',
  saveResults: 'Save Results',
  failedToSaveRankings: 'Failed to save rankings',

  // Standings
  standings: 'Standings',
  championshipStandings: 'Championship Standings',
  qualificationStandings: 'Qualification Standings',
  juniorStandings: 'Junior Championship',
  seniorStandings: 'Senior Championship',
  position: 'Pos.',
  name: 'Name',
  totalPoints: 'Total',
  racesParticipated: 'Races',
  bestResult: 'Best Result',
  qualificationMasterStandings: 'Qualification Master Standings',
  allQualificationPoints: 'ALL Qualification Points',
  pureQualificationChampionship: 'Pure Qualification Championship:',
  allQualificationPointsCounted: 'All qualification points counted (no drops)',
  juniorChampionshipStandings: 'Junior Championship Standings',
  seniorChampionshipStandings: 'Senior Championship Standings',
  ageAndUnder: 'and under',
  agePlus: '+',
  juniorDriversOnly: 'Junior Drivers:',
  seniorDriversOnly: 'Senior Drivers:',
  onlyDriversAgedIncluded: 'Only drivers aged',
  andUnderIncluded: 'and under included',
  andOverIncluded: '+ included',
  noStandingsYet: 'No standings yet.',
  noQualificationDataYet: 'No qualification data yet.',
  noJuniorDriversYet: 'No junior drivers with standings yet.',
  noSeniorDriversYet: 'No senior drivers with standings yet.',
  noRacesYet: 'No races yet.',
  noResultsYet: 'No results yet',
  driversCount: 'drivers',
  born: 'Born:',
  dropped: 'dropped',
  raceShort: 'Race',
  qualShort: 'Qual',
  points: 'pts',
  total: 'Total',

  // Export & Import
  exportExcel: 'Export Excel',
  exportChampionships: 'Export Championships to Excel',
  selectSeriesToExport: 'Select series to export:',
  selectAtLeastOne: 'Select at least one series',
  selected: 'selected',
  export: 'Export',
  exportLanguage: 'Export Language',
  preview: 'Preview',
  importing: 'Importing...',
  chooseExcelFile: 'Choose Excel File',
  excelFileFormat: 'Excel file format:',
  columnDriver: 'Column: Driver or Name (driver name)',
  columnPosition: 'Column: Position or Pos (race position: 1-50)',
  columnQual: 'Column: Qual or Q (optional, qualification position: 1-5)',
  driversNotInSeriesCreated: 'Drivers not in your series will be automatically created.',
  resultsReadyToImport: 'result(s) ready to import',
  newDriversWillBeCreated: 'new driver(s) will be created:',
  rowsSkipped: 'row(s) skipped:',
  andMore: 'and {count} more',
  rememberToAddClubAndBirthYear: '💡 After import: Remember to add club and birth year information to new drivers',
  tipAddClubAndBirthYear: '💡 Tip: You can now add club and birth year information to these drivers using the edit button (pencil icon).',
  noDataFoundInExcel: 'No data found in Excel file',
  missingDriverNameOrPosition: 'Missing driver name or position',
  successfullyParsedResults: '✓ Successfully parsed {count} result(s) from Excel file',
  errorReadingExcel: 'Error reading Excel file: ',
  importResults: 'Import {count} Result(s)',
  importFailed: 'Import failed: ',
  new: 'NEW',
  newDrivers: 'new drivers',
  howToImportFromMyRCM: 'How to import from MyRCM:',
  myrcmStep1: 'Open your race results on MyRCM',
  myrcmStep2: 'Copy the event URL (e.g., https://www.myrcm.ch/en/report/98862/393406)',
  myrcmStep3: 'Paste it below - the system will automatically fetch both finals and qualification',
  myrcmStep4: 'If the race has only qualification, tick "Import qualification as final"',
  myrcmStep5: 'Click "Fetch & Preview"',
  myrcmStep6: 'Review driver matches - fix any mismatches',
  myrcmStep7: 'Click "Import Results"',
  checkMatch: 'CHECK',
  matchedByCarNumberOnlyHint: 'Matched on car number alone, not on name - car numbers are per class, so confirm this is the right driver',
  browseMyrcmEvents: 'Browse events',
  pasteUrlInstead: 'Paste URL',
  myrcmBrowseStep1: 'Pick an event from the list (filter by country or search by name)',
  myrcmBrowseStep2: 'Pick the class',
  myrcmBrowseStep3: 'Click "Fetch & Preview"',
  myrcmScopeArchive: 'Past events',
  myrcmScopeLive: 'Running now',
  myrcmFilterCountry: 'Filter by country',
  myrcmAllCountries: 'All countries',
  myrcmSearchEventsPlaceholder: 'Search event, organizer or location...',
  myrcmSearch: 'Search',
  myrcmResults: 'Results',
  myrcmPreviousPage: 'Previous',
  myrcmNextPage: 'Next',
  myrcmLoadingEvents: 'Loading events...',
  myrcmNoEventsFound: 'No events found',
  myrcmFailedToLoadEvents: 'Could not load the event list',
  myrcmNoReportsBadge: 'no results',
  myrcmSelectClass: 'Select class',
  myrcmLoadingClasses: 'Loading classes...',
  myrcmNoClassesForEvent: 'This event has no result classes',
  myrcmFailedToLoadClasses: 'Could not load the class list',
  myrcmBackToEvents: 'Back to events',
  myrcmSelectedEvent: 'Selected',
  myrcmChangeSelection: 'Change',
  myrcmSiteChanged: 'The MyRCM page layout has changed and could not be read',
  myrcmUsePasteInstead: 'Paste a URL instead',
  myrcmResultsUrlLabel: 'MyRCM Results URL',
  urlWillBeUsedForBothFinalAndQual: 'This URL will be used for both final and qualification results',
  enterTheBaseUrlWithoutReportKey: 'Enter the base URL (reportKey will be added automatically)',
  finalResultsUrl: 'Final Results URL',
  qualificationResultsUrl: 'Qualification Results URL',
  optional: '(Optional)',
  fetchingFromMyRCM: 'Fetching from MyRCM...',
  fetchAndPreview: 'Fetch & Preview Results',
  hideDriverMapping: 'Hide',
  showDriverMapping: 'Show',
  driverMapping: 'Driver Mapping',
  matchDrivers: '🔗 Match Drivers',
  someDriversNotMatched: 'Some drivers couldn\'t be matched automatically. Select existing drivers or create new ones:',
  createNewDriver: '➕ Create New Driver (#{carNumber})',
  matchToExistingDriver: 'Match to Existing Driver',
  unmatched: 'UNMATCHED',
  matched: 'MATCHED',
  myrcmLabel: 'MyRCM: ',
  pleaseEnterFinalResultsUrl: 'Please enter a Final Results URL',
  failedToFetchMyRCM: 'Failed to fetch MyRCM page',
  couldNotFindResultsTable: 'Could not find results table on the page',
  noDriversFoundInTable: 'No drivers found in the results table. Check browser console for details.',
  successfullyFetchedFromMyRCM: '✓ Successfully fetched {count} result(s) from MyRCM',
  driversNeedManualMatching: '⚠ {count} driver(s) need manual matching or will be created as new',
  pos: 'Pos',
  qPos: 'Q Pos',
  qPts: 'Q Pts',
  pts: 'Pts',
  importQualificationAsFinal: 'Import qualification as final results',
  importQualificationAsFinalDescription: 'Use this option when the race has only qualification without finals',

  // Table headers for export
  positionShort: 'Pos.',
  nameColumn: 'Name',
  clubColumn: 'Club',
  totalColumn: 'Total',

  // Language selector
  language: 'Language',
  finnish: 'Finnish',
  english: 'English',
  swedish: 'Swedish',

  // Help System
  help: 'Help',
  helpPublicTitle: 'Welcome to RC Racing Series',
  helpPublicDescription: 'This system displays RC racing championship standings. You can browse different series, years, and countries.',
  helpViewingStandings: 'Viewing Standings',
  helpSelectCountry: 'Select a country from the menu in the top right',
  helpSelectYear: 'Select a year from the dropdown',
  helpSelectSeries: 'Select a series from the dropdown',
  helpViewTabs: 'Browse tabs to see championship standings, qualification standings, and other categories',
  helpStandingsTip: 'Results update automatically after races conclude. Dropped results are shown with transparent font.',
  helpUnderstandingStandings: 'Understanding Standing Categories',
  helpChampionshipExplanation: 'Main results from all races, with possible drops',
  helpQualificationExplanation: 'Bonus points from TOP 5 qualification results (5,4,3,2,1 points)',
  helpJuniorExplanation: 'Separate championship for young drivers (defined age limit)',
  helpSeniorExplanation: 'Separate championship for older drivers (defined age limit)',
  helpRaceResults: 'Race Results',
  helpRaceResultsDescription: 'Each race shows positions, points, and qualification positions. Click races to see detailed results.',
  helpAdminTitle: 'Admin Panel Guide',
  helpAdminDescription: 'Here you can manage series and their classes, drivers, races, results, and events with online entry.',
  helpManagingSeries: 'Managing Series',
  helpSeriesDescription: 'Series are championship seasons. Each series can span one or more years.',
  helpCreateSeries: 'Create a new series by providing name, country, start and end year',
  helpConfigureSeries: 'Configure series settings (drops, qualification points, etc.)',
  helpAddDrivers: 'Add drivers to a class, manually or copied from another class',
  helpAddRaces: 'Add races to a class and import results',
  helpSeriesSettingsTip: 'All setting changes are saved automatically and results update immediately.',
  helpManagingDrivers: 'Managing Drivers',
  helpDriversDescription: 'Drivers belong to a class. Add them to a class before entering results.',
  helpAddDriverManually: 'Add a driver by clicking "Add Driver" and filling in the details',
  helpCopyDrivers: 'Copy drivers from another series to save time',
  helpEditDriverInfo: 'Update driver information (name, club, birth year) using the pencil icon',
  helpDriversTip: 'Birth year is needed for junior and senior championships. Car number is optional but recommended.',
  helpManagingRaces: 'Managing Races',
  helpRacesDescription: 'A race belongs to a class and holds a date, name, and results.',
  helpCreateRace: 'Create a race by providing date, name, and identifier',
  helpAddResultsManually: 'Add results manually or import them',
  helpImportingResults: 'Importing Results',
  helpExcelImport: 'Import results from Excel file (required columns: Driver/Name, Position/Pos, Qual/Q)',
  helpMyRCMImport: 'Import results directly from MyRCM system using URL',
  helpRacesTip: 'After import, verify all drivers are matched correctly. You can fix incorrect matches before importing.',
  helpExcelFormat: 'Excel File Format',
  helpExcelFormatDescription: 'Your Excel file should have at least these columns:',
  helpExportingData: 'Exporting Data',
  helpExportDescription: 'You can export championship results to Excel file for sharing.',
  helpOpenExportDialog: 'Click "Export Excel" from the menu',
  helpSelectExportSeries: 'Select series to export (you can select multiple)',
  helpChooseExportLanguage: 'Choose export language',
  helpDownloadExcel: 'Click "Export" to download the Excel file',
  helpSeriesSettingsDetail: 'Series Settings Details',
  helpTotalRacesDetail: 'Specify how many races the series will have in total.',
  helpRacesCountedDetail: 'Specify how many best results count towards the championship. Once more races have been run than this number, the worst results are dropped automatically. E.g., a series with 4 total races and 3 counted races counts the 3 best results and drops the worst one.',
  helpQualificationPointsDetail: 'When enabled, TOP 5 qualification results get bonus points (5,4,3,2,1). These points are added to main results.',
  helpQualificationMasterDetail: 'Create a separate championship from qualification points only (no drops). Shows who is the best qualifier.',
  helpAgeChampionshipsDetail: 'Create separate championships for age groups. Define age limit (e.g., 18 for juniors, 40 for seniors).',
  helpCommonFeatures: 'Common Features',
  helpLanguageDescription: 'Change interface language (Finnish, English, Swedish)',
  helpCountryDescription: 'Filter series by country to see only that country\'s races',
  helpYearDescription: 'Select year to see that season\'s series',
  helpNeedMoreHelp: 'Need More Help?',
  helpContactDescription: 'If you have questions or issues, contact the system administrator.',
  helpVersion: 'Version',
  helpSystemName: 'System Name',
  // Help - classes, calendar, online entry, imports (added 2026-09)
  helpSelectClass: 'Pick a class within the series — each class has its own standings',
  helpRaceCalendar: 'Race Calendar',
  helpRaceCalendarDescription: 'The calendar lists upcoming and past events. Open an event to see its details, classes, and entrant list.',
  helpCalendarColours: 'Each event is coloured by its entry status: entry open, not open yet, or closed.',
  helpEntering: 'Entering a Race',
  helpEnteringDescription: 'When an event takes entries on this site, open it and press Enter.',
  helpEnterPickClasses: 'Pick the classes you want to race and give a transponder number for each — you can leave it blank and sort it out at the track.',
  helpEnterDetails: 'Fill in your name, club, car number, and contact details.',
  helpEnterExtras: 'Answer the organizer\'s extra questions — for example a lunch order or a tyre choice. Some questions appear only once you answer a related one.',
  helpEnterSaveLink: 'Save the link you are given. It is not emailed, and it is the only way to edit or withdraw your entry later.',
  helpEnteringTip: 'If a class is full you go on the waiting list, and the organizer will be in touch.',
  helpClasses: 'Series and Classes',
  helpClassesDescription: 'A series is the championship season. Its classes (for example 1:8 Nitro, 1:10 Electric) are where drivers, races, and results actually live — so create a class under a series first, then add drivers and races to that class.',
  helpEvents: 'Events and Online Entry',
  helpEventsDescription: 'An event is a race meeting people can enter online. Organizers can create and edit their own events.',
  helpEventCreate: 'Create an event with its name, location, dates, and classes.',
  helpEventEntryWindow: 'Set the entry window — entry opens and closes on the dates you choose, and the calendar shows that status.',
  helpEventQuestions: 'Add extra questions for entrants (lunch, tyre choice, and so on). A question can be set to appear only when another is answered yes.',
  helpEventExternalEntry: 'If entry is handled by another system, add its address and the entry button sends people there instead.',
  helpEventEntrantList: 'Download the entrant list, including the answers to your extra questions and their totals.',
  helpAddAllClasses: 'Use "Add all from series" to add every class of a championship to an event in one press.',
  helpEventCopy: 'Copy an event to reuse its classes and questions — everything is copied except the dates.',
  helpCopyRace: 'Copy a race to next year to carry its setup into the new season, then set the new dates.',
  helpLiveRCImport: 'Import results directly from LiveRC using the event address — every class in the event is read from it.',
  // Events / calendar
  eventManagement: 'Event management',
  eventsTab: 'Calendar',
  addEvent: 'Add event',
  editEvent: 'Edit event',
  deleteEvent: 'Delete event',
  eventName: 'Event name',
  eventOrganizer: 'Organizer',
  eventVenue: 'Track',
  eventAddress: 'Address',
  eventCity: 'City',
  eventCountry: 'Country',
  eventChampionship: 'Championship',
  championshipNone: 'Ordinary race',
  championshipEuropean: 'European Championship',
  championshipWorld: 'World Championship',
  championshipHint: 'A championship appears on every country\'s calendar, not only its own.',
  championshipHostedIn: 'Hosted in',
  championshipReadOnly: 'Run by {country}',
  eventStartDate: 'Start date',
  eventEndDate: 'End date',
  eventEndDateOptional: 'End date (optional)',
  eventDescription: 'Description',
  eventStatus: 'Status',
  eventStatusDraft: 'Draft',
  eventStatusPublished: 'Published',
  eventStatusCancelled: 'Cancelled',
  eventEntryOpens: 'Entry opens',
  eventEntryCloses: 'Entry closes',
  eventEntryInstructions: 'Entry instructions',
  eventEntryListPublic: 'Show entrants publicly',
  eventEntryListPublicDescription: 'The public view shows only name, club, car number and classes.',
  eventSupportedTransponders: 'Supported transponders',
  eventContactEmail: 'Contact email',
  eventContactPhone: 'Contact phone',
  eventWebsite: 'Website',
  eventOwner: 'Owner',
  eventClasses: 'Classes',
  eventAddClass: 'Add class',
  eventClassName: 'Class name',
  eventClassSeries: 'Series',
  eventClassNoSeries: 'No series',
  eventClassEntryLimit: 'Entry limit',
  eventClassLinkedResults: 'Results linked',
  eventClassRoundReady: 'Round ready',
  eventClassRoundReadyHelp: 'This class has its round in the series. Results can be imported into it once the racing is done.',
  eventNoClasses: 'No classes yet',
  // Extras - the questions an organizer adds to their own entry form.
  // The organizer's own wording for each question is stored as typed and is
  // never translated; only the scaffolding around it is.
  eventExtras: 'Extras and pre-orders',
  eventExtrasHelp: 'Anything else you need to ask an entrant: lunch, tyre sets, pit space. Quantities only, no prices.',
  eventNoExtras: 'Nothing extra asked',
  eventAddExtra: 'Add question',
  extraLabel: 'What do you want to ask?',
  extraHelpText: 'Help text (optional)',
  extraType: 'Answer',
  extraTypeYesNo: 'Yes / no',
  extraTypeNumber: 'Number',
  extraTypeText: 'Free text',
  extraRequired: 'Must be answered',
  extraAlwaysAsked: 'Always asked',
  extraOnlyWhen: 'only when: {question}',
  extraDependencyLocked: 'Another question depends on this one, so it must stay a yes/no and cannot itself be conditional.',
  extraMinValue: 'Least',
  extraMaxValue: 'Most',
  extraLabelRequired: 'Write the question first',
  extraLabelTooLong: 'That question is too long',
  extraTypeLocked: 'People have answered this already, so the answer type is fixed. Add a new question instead.',
  extraAnswered: '{count} answered',
  deleteExtraConfirm: 'Delete this question?',
  deleteExtraDiscardConfirm: '{count} people have answered this. Delete it anyway and discard their answers?',
  failedToSaveExtra: 'Failed to save the question',
  failedToDeleteExtra: 'Failed to delete the question',
  entryExtras: 'Extras',
  entryExtrasHelp: 'The organizer would like to know:',
  entrantExtrasTotals: 'Ordered in total',
  // Adding a whole championship's classes at once.
  eventAddAllFromSeries: 'Add all from series',
  eventAddAllFromSeriesHelp: 'Adds every class of the chosen series to this event, skipping any it already has.',
  eventPickSeriesFirst: 'Choose a series first',
  eventAddAllFromSeriesDone: '{created} classes added',
  eventAddAllFromSeriesSkipped: '{skipped} were already here',
  eventAddAllFromSeriesNone: 'This event already has every class of that series',
  // Copying an event.
  eventCopy: 'Copy',
  eventCopyTitle: 'Copy event',
  eventCopySuffix: '{name} (copy)',
  eventCopyDatesCleared: 'Everything is copied except the dates. Fill in the race dates and the entry window for the new event.',
  eventCopyCarries: '{classes} classes and {extras} extras will be copied. You can edit them once it is saved.',
  noEvents: 'No events',
  eventNameRequired: 'Event name is required',
  eventStartDateRequired: 'Start date is required',
  eventClassNameRequired: 'Class name is required',
  deleteEventConfirm: 'Delete this event?',
  deleteClassConfirm: 'Delete this class?',
  failedToLoadEvents: 'Failed to load events',
  failedToSaveEvent: 'Failed to save event',
  failedToDeleteEvent: 'Failed to delete event',
  failedToSaveClass: 'Failed to save class',
  failedToDeleteClass: 'Failed to delete class',
  eventMineOnly: 'Mine only',
  eventAllYears: 'All years',
  eventBasicInfo: 'Basic information',
  eventLocation: 'Location',
  eventEntrySettings: 'Entry',
  eventExternalEntry: 'Entry is handled by another system',
  eventExternalEntryHint: 'The entry button sends people to that system instead. This site will not collect entries for this event.',
  eventExternalEntryUrl: 'Entry address',
  entryRegisterExternally: 'Register at',
  entryHandledElsewhere: 'Entry for this race is handled outside this site.',
  externalEntryNeedsUrl: 'Give the address entrants should be sent to.',
  // Roles
  organizer: 'Organizer',
  competitor: 'Competitor',
  organizerRole: 'Organizer:',
  organizerDescription: 'Can create and edit their own events and download their entrant lists. No access to series or results.',
  competitorRole: 'Competitor:',
  competitorDescription: 'Can view and edit their own details and their own entries.',
  // Public calendar
  calendar: 'Race calendar',
  calendarTitle: 'Race calendar',
  calendarBack: 'Back to calendar',
  calendarNoEvents: 'No events match these filters',
  calendarAllCountries: 'All countries',
  calendarLoading: 'Loading events...',
  calendarFailedToLoad: 'Failed to load the race calendar',
  calendarLegacyRace: 'Series round',
  calendarEntryOpen: 'Entry open',
  calendarEntryNotYetOpen: 'Entry not open yet',
  calendarEntryClosed: 'Entry closed',
  calendarEntrants: 'entrants',
  calendarEventNotFound: 'Event not found',
  eventDetails: 'Event details',
  eventEntrantList: 'Entrants',
  eventNoEntrants: 'No entrants yet',
  eventEntrantListHidden: 'The organizer does not publish the entrant list.',
  eventCarNumber: 'Car number',
  eventClub: 'Club',
  eventEntryLimitReached: 'Full',
  eventCancelled: 'This event has been cancelled.',
  eventNoTransponders: 'Not specified',
  eventContact: 'Contact',
  calendarMonths: 'Jan,Feb,Mar,Apr,May,Jun,Jul,Aug,Sep,Oct,Nov,Dec',
  // Month grid
  calendarViewGrid: 'Month',
  calendarViewList: 'List',
  calendarPrevMonth: 'Previous month',
  calendarNextMonth: 'Next month',
  calendarToday: 'Today',
  calendarWeekdays: 'Mon,Tue,Wed,Thu,Fri,Sat,Sun',
  calendarMonthsFull: 'January,February,March,April,May,June,July,August,September,October,November,December',
  calendarMoreEvents: '+{count} more',
  calendarNoEventsThisMonth: 'No events this month',
  eventClassSeriesLocked: 'The series cannot be changed because results are already linked to this class.',
  eventClassSaved: 'Class updated',

  // Entries
  entryEnter: 'Enter',
  entryTitle: 'Entry',
  entryFirstName: 'First name',
  entryLastName: 'Last name',
  entryTransponderNumber: 'Transponder number',
  entryClassTransponder: 'Transponder',
  entryTransponderPerClassHint: 'Give the transponder number you will use in each class. You can leave it blank and sort it out at the track.',
  transponderNumberPlaceholder: 'e.g. 0012345',
  entryCarNumber: 'Car number',
  entryClub: 'Club',
  entryBirthYear: 'Birth year',
  entryEmail: 'Email',
  entryPhone: 'Phone',
  entryNotes: 'Notes',
  entrySelectClasses: 'Select classes',
  entryOptional: 'optional',
  entrySubmit: 'Submit entry',
  entrySubmitting: 'Submitting...',
  entrySuccessTitle: 'Entry received',
  entrySuccessWaitlisted: 'The class is full, so you are on the waiting list. The organizer will be in touch.',
  entryManageLinkTitle: 'Save this link',
  entryManageLinkHelp: 'This link lets you edit or withdraw your entry. It is not sent by email, so keep it now.',
  entryCopyLink: 'Copy link',
  entryLinkCopied: 'Copied',
  entryMyEntriesOnDevice: 'Entries made on this device',
  entryManageTitle: 'Your entry',
  entryWithdraw: 'Withdraw entry',
  entryWithdrawConfirm: 'Withdraw this entry?',
  entryWithdrawn: 'Your entry has been withdrawn.',
  entryUpdated: 'Your details were saved.',
  entryNotFound: 'Entry not found. Check the link.',
  entryClosedForChanges: 'Entry has closed. Please contact the organizer.',
  entryHumanCheck: 'Confirm you are human',
  entryTurnstileMissing: 'Bot protection is not configured, so entry is unavailable.',
  entryDuplicateTransponder: 'That transponder number is already used at this event. Please check it.',
  entryPickAtLeastOneClass: 'Pick at least one class',
  entryFailed: 'Entry failed',
  entryStatusRegistered: 'Registered',
  entryStatusWaitlisted: 'Waiting list',
  entryStatusCancelled: 'Withdrawn',

  // Signup during entry
  entryCreateAccount: 'Create an account at the same time',
  entryCreateAccountHelp: 'An account lets you manage your entries later without a separate link, and fills your details in next time.',
  entryPassword: 'Password',
  entryPasswordHint: 'At least 8 characters',
  entryAccountCreated: 'Account created - you are now signed in.',
  entryPasswordTooShort: 'Password must be at least 8 characters',
  entrySignedInAs: 'Signed in as',

  // Entrant list
  entrantList: 'Entrants',
  entrantListOpen: 'View entrants',
  entrantCount: 'entrants',
  entrantNone: 'No entrants yet',
  entrantExport: 'Download for timing',
  entrantExportFormat: 'Timing system',
  entrantExportExcel: 'Download Excel',
  entrantExportCsv: 'Download CSV',
  entrantFilterAll: 'All',
  entrantTransponder: 'Transponder',
  entrantContact: 'Contact',
  entrantStatus: 'Status',
  entrantFailedToLoad: 'Failed to load entrants',
  entrantExportFailed: 'Download failed',
  entrantNoTransponder: 'no transponder',
  entrantRemove: 'Delete entry',
  entrantRemoveConfirm: 'Permanently delete this entry?',

  // Competitor account
  myTransponders: 'My transponders',
  myTranspondersHint: 'This list fills itself as you enter races: a new number is saved here and offered back the next time.',
  transponderAdd: 'Add',
  transponderLabelField: 'Name (optional)',
  transponderRemove: 'Remove from list',
  transponderNoneSaved: 'You have not saved any transponders yet.',
  accountTitle: 'My details',
  accountProfile: 'Basic information',
  accountMyEntries: 'My entries',
  accountNoEntries: 'You have not entered any races yet',
  accountSaved: 'Your details were saved',
  accountSaveFailed: 'Could not save your details',
  accountLoadFailed: 'Could not load your details',
  accountPrefillHelp: 'These details are filled in for you when you enter a race.',
  accountBrowseCalendar: 'Browse the race calendar',
  accountEntryClosed: 'Entry closed',
  accountManageEntry: 'Edit',
  accountUsername: 'Username',

  // Linking results to a series
  linkResults: 'Enable results',
  linkResultsHelp: 'Creates a round in the series for this class\'s results. Do this when you are ready to record results.',
  linkResultsDone: 'Results linked to the series',
  linkResultsNoSeries: 'Link the class to a series first',
  syncDrivers: 'Turn entrants into drivers',
  syncDriversHelp: 'Creates drivers in the series from the entrants, so results can be recorded against them.',
  syncDriversResult: '{created} created, {matched} matched, {already} already linked',
  syncDriversConflicts: 'Conflicts',
  syncDriversConflictNumber: 'car number already belongs to another driver in this series',
  linkFailed: 'That did not work',
  entryLoginIsEmail: 'Login (email address)',
  entryLoginIsEmailHint: 'You will sign in with this email address. No separate username is needed.',
  entryEmailRequiredForAccount: 'An email address is required to create an account',
  entryEmailTaken: 'An account already exists for that email address',

  // Password reset
  forgotPassword: 'Forgot your password?',
  forgotPasswordTitle: 'New password',
  forgotPasswordHelp: 'Enter your email address and we will send a link for setting a new password.',
  forgotPasswordSend: 'Send the link',
  forgotPasswordSent: 'If that address has an account, a link is on its way. Check your spam folder too.',
  forgotPasswordCompetitorsOnly: 'This is for competitor accounts. Staff passwords are reset by an administrator.',
  resetPasswordTitle: 'Set a new password',
  resetPasswordNew: 'New password',
  resetPasswordConfirm: 'Confirm password',
  resetPasswordSubmit: 'Save password',
  resetPasswordDone: 'Your password has been changed. You can sign in now.',
  resetPasswordExpired: 'That link is no longer valid. Please request a new one.',
  resetPasswordMismatch: 'The passwords do not match',
  resetPasswordChecking: 'Checking the link...',
  backToLogin: 'Back to sign in',
};

export default en;
